import {useMemo} from "react";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {AvailabilityType} from "crqlar-react-common/dist/models/availability/availability.model";
import {AvailabilityCalculationResult} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import {getSectorIcon} from "crqlar-react-common/dist/components/sectorIcons/sectorIcons";

export const useShouldDisplayChildrenResources = (sector?: Sector) => useMemo(() => {
    return sector?.sectorType === SectorType.RESTAURANT
}, [sector])

/**
 * Returns true when the reservation's time range is NOT fully contained within any OPEN
 * availability ("open availability section") of its assigned resource(s).
 *
 * Only considers OPEN availability blocks that belong to one of the reservation's resources
 * (matched by entityId). If there is no OPEN availability info for those resources we return
 * false (nothing to warn about) so resources that are simply always-open never trigger a
 * false positive.
 */
export const isReservationOutsideOpenAvailability = (
    reservation: Reservation | undefined,
    availabilityResults: AvailabilityCalculationResult[] | undefined
): boolean => {
    if (!reservation?.expectedStartDate || !reservation?.expectedEndDate) return false

    const resourceIds = reservation.resourceIds ?? []
    if (!resourceIds.length) return false

    const openBlocks = (availabilityResults ?? []).filter(
        a => a.type === AvailabilityType.OPEN && resourceIds.includes(a.entityId ?? '')
    )

    // No OPEN availability defined for these resources -> can't be "outside" of it
    if (!openBlocks.length) return false

    const resStart = new Date(reservation.expectedStartDate).getTime()
    const resEnd = new Date(reservation.expectedEndDate).getTime()

    const withinOpen = openBlocks.some(a => {
        const start = new Date(a.startTime ?? 0).getTime()
        const end = new Date(a.endTime ?? 0).getTime()
        return resStart >= start && resEnd <= end
    })

    return !withinOpen
}

export const useCanSliceByPartOfDay = (sector?: Sector) => useMemo(() => {
    return sector?.sectorType !== SectorType.ACCOMMODATION
}, [sector])

export const useSectorIcon = (sector?: Sector) => useMemo(() => {
    const Icon = getSectorIcon(sector) ?? EventOutlinedIcon;
    return <Icon />;
}, [sector])

export type ReservationScheduleTab = 'action-center' | 'daily' | 'calendar' | 'floorplan' | 'list' | 'halfboard'

export const statusFilterOptionsBasedOnView = (view: ReservationScheduleTab) => {
    switch (view) {
        case 'action-center':
            return []
        case 'daily':
            return []
        case 'calendar':
            return [
                ReservationStatus.PENDING,
                ReservationStatus.CONFIRMED,
                ReservationStatus.ACTIVE,
                ReservationStatus.COMPLETED,
                ReservationStatus.CANCELLED,
                ReservationStatus.NO_SHOW,
            ]
        case 'floorplan':
            return [
                ReservationStatus.PENDING,
                ReservationStatus.CONFIRMED,
                ReservationStatus.ACTIVE,
                ReservationStatus.COMPLETED,
                ReservationStatus.CANCELLED,
                ReservationStatus.NO_SHOW,
            ]
        case 'list':
            return [
                ReservationStatus.PENDING,
                ReservationStatus.CONFIRMED,
                ReservationStatus.ACTIVE,
                ReservationStatus.COMPLETED,
                ReservationStatus.CANCELLED,
                ReservationStatus.NO_SHOW,
                ReservationStatus.OPTION,
                ReservationStatus.UNCOMPLETED
            ]
        case 'halfboard':
            return []
        default:
            return [
                ReservationStatus.PENDING,
                ReservationStatus.CONFIRMED,
                ReservationStatus.ACTIVE,
                ReservationStatus.COMPLETED,
                ReservationStatus.CANCELLED,
                ReservationStatus.NO_SHOW,
                ReservationStatus.OPTION,
                ReservationStatus.UNCOMPLETED
            ]
    }
}

export const defaultStatusFiltersBasedOnView = (view: ReservationScheduleTab) => {
    if (view === 'floorplan') {
        return [
            ReservationStatus.PENDING,
            ReservationStatus.CONFIRMED,
            ReservationStatus.ACTIVE,
            ReservationStatus.COMPLETED,
        ]
    }
    return statusFilterOptionsBasedOnView(view)
}

export const permittedStepsPerTimelineSpan: Record<string, number[]> = {
    'day': [15, 30, 60, 120, 240, 360, 720, 1440],
    'week': [15, 30, 60, 120, 240, 360, 720, 1440],
    'month': [60, 120, 240, 360, 720, 1440]
}

const defaultTimelineSteps = [60, 120, 240, 360, 720, 1440]

export const getPermittedSteps = (key: string | undefined): number[] => {
    return key ? permittedStepsPerTimelineSpan[key] : defaultTimelineSteps
}
